import { useEffect, useReducer, useRef } from 'react';
import { useLocation } from 'react-router';
import type { AppEvent } from 'src/common/events/appEvents';
import { useAppEventListener } from 'src/common/events/appEvents';
import { useAppContainerContext } from '../AppContainerContext';
import { actionRegistry } from './actions/registry';
import type { SteeringActionType, SteeringEvent, SteeringResponse, SteeringState } from './types';
import { SteeringEventType, SteeringPhase } from './types';
import { useNextBestAction } from './useNextBestAction';

const STORAGE_KEY = 'steering-state';

type PersistedState = {
  phase: typeof SteeringPhase.FOLLOW_UP;
  followUp: { title: string; message: string };
  actionType: SteeringActionType;
  targetHref: string;
  completionEvents: AppEvent[];
};

const initialState: SteeringState = {
  phase: SteeringPhase.SUGGESTION,
  response: null,
  followUp: null,
  actionType: null,
  targetHref: null,
  completionEvents: [],
};

export function steeringReducer(state: SteeringState, event: SteeringEvent): SteeringState {
  switch (event.type) {
    case SteeringEventType.SUGGESTION_RECEIVED:
      return {
        phase: SteeringPhase.SUGGESTION,
        response: event.response,
        followUp: null,
        actionType: event.response.action,
        targetHref: null,
        completionEvents: event.completionEvents,
      };
    case SteeringEventType.ACTION_CLICKED:
      return {
        phase: SteeringPhase.FOLLOW_UP,
        response: state.response,
        followUp: event.followUp,
        actionType: state.actionType,
        targetHref: event.href,
        completionEvents: state.completionEvents,
      };
    case SteeringEventType.REHYDRATE:
      return {
        phase: SteeringPhase.FOLLOW_UP,
        response: event.response,
        followUp: event.followUp,
        actionType: event.response.action,
        targetHref: event.href,
        completionEvents: event.completionEvents,
      };
    case SteeringEventType.APP_EVENT_RECEIVED:
      if (state.phase === SteeringPhase.FOLLOW_UP && state.completionEvents.includes(event.event)) {
        return initialState;
      }
      return state;
    case SteeringEventType.RESET:
      return initialState;
  }
}

export function useSteering() {
  const location = useLocation();
  const { contentMeta, setSidebarCollapsed, setPreventSidebarCollapse } = useAppContainerContext();
  const clientId = contentMeta?.entityId ?? null;
  const { data, isLoading } = useNextBestAction();
  const [state, rawDispatch] = useReducer(steeringReducer, undefined, loadPersistedState);

  const stateRef = useRef(state);
  stateRef.current = state;

  const dispatch = useRef((event: SteeringEvent) => {
    if (event.type === SteeringEventType.ACTION_CLICKED) {
      if (stateRef.current.actionType === null) {
        throw new Error('Cannot persist follow-up: actionType is null');
      }
      const persisted: PersistedState = {
        phase: SteeringPhase.FOLLOW_UP,
        followUp: event.followUp,
        actionType: stateRef.current.actionType,
        targetHref: event.href,
        completionEvents: stateRef.current.completionEvents,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
    }
    if (
      event.type === SteeringEventType.RESET ||
      event.type === SteeringEventType.APP_EVENT_RECEIVED
    ) {
      const nextState = steeringReducer(stateRef.current, event);
      if (nextState.phase === SteeringPhase.SUGGESTION) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    rawDispatch(event);
  }).current;

  useEffect(() => {
    if (!data) return;
    const handler = actionRegistry[data.action];
    if (state.phase === SteeringPhase.SUGGESTION && state.response !== data) {
      dispatch(suggestionReceived(data, handler.completionEvents));
    }
    if (
      state.phase === SteeringPhase.FOLLOW_UP &&
      state.response === null &&
      state.followUp &&
      state.targetHref
    ) {
      dispatch(rehydrate(data, handler.completionEvents, state.followUp, state.targetHref));
    }
  }, [data, state.phase, state.response, state.followUp, state.targetHref, dispatch]);

  const inFollowUp = state.phase === SteeringPhase.FOLLOW_UP;

  useEffect(() => {
    if (inFollowUp) {
      setPreventSidebarCollapse(true);
      setSidebarCollapsed(false);
    } else {
      setPreventSidebarCollapse(false);
    }
    return () => setPreventSidebarCollapse(false);
  }, [inFollowUp, setSidebarCollapsed, setPreventSidebarCollapse]);

  const hasNavigated = useRef(false);
  const targetPathname = state.targetHref?.split('?')[0] ?? null;
  useEffect(() => {
    if (state.phase !== SteeringPhase.FOLLOW_UP) {
      hasNavigated.current = false;
      return;
    }

    if (!hasNavigated.current) {
      hasNavigated.current = true;
      if (state.response === null && location.pathname !== targetPathname) {
        dispatch(reset());
      }
      return;
    }

    if (location.pathname !== targetPathname) {
      dispatch(reset());
    }
  }, [location.pathname, state.phase, state.response, targetPathname, dispatch]);

  useAppEventListener(event => {
    dispatch(appEventReceived(event));
  });

  function handleSuggestionClick() {
    if (!state.response) return;
    const handler = actionRegistry[state.response.action];
    const followUp = handler.getFollowUp(state.response.attributes);
    const href = handler.getHref(state.response.attributes, clientId);
    dispatch(actionClicked(followUp, href));
    return href;
  }

  return { state, isLoading, inFollowUp, handleSuggestionClick };
}

export function suggestionReceived(
  response: SteeringResponse,
  completionEvents: AppEvent[],
): SteeringEvent {
  return { type: SteeringEventType.SUGGESTION_RECEIVED, response, completionEvents };
}

export function actionClicked(
  followUp: { title: string; message: string },
  href: string,
): SteeringEvent {
  return { type: SteeringEventType.ACTION_CLICKED, followUp, href };
}

export function rehydrate(
  response: SteeringResponse,
  completionEvents: AppEvent[],
  followUp: { title: string; message: string },
  href: string,
): SteeringEvent {
  return { type: SteeringEventType.REHYDRATE, response, completionEvents, followUp, href };
}

export function appEventReceived(event: AppEvent): SteeringEvent {
  return { type: SteeringEventType.APP_EVENT_RECEIVED, event };
}

export function reset(): SteeringEvent {
  return { type: SteeringEventType.RESET };
}

function loadPersistedState(): SteeringState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const parsed: PersistedState = JSON.parse(raw);
    if (parsed.phase === SteeringPhase.FOLLOW_UP && parsed.followUp) {
      return {
        phase: SteeringPhase.FOLLOW_UP,
        response: null,
        followUp: parsed.followUp,
        actionType: parsed.actionType,
        targetHref: parsed.targetHref,
        completionEvents: parsed.completionEvents ?? [],
      };
    }
  } catch {
    /* corrupted data */
  }
  return initialState;
}
